import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getAccessToken } from '../../utils/auth';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import daLocale from 'date-fns/locale/da';
import { saveAs } from 'file-saver';
import Decimal from 'decimal.js';

function getDescription(entry) {
  if (entry.type === '51') {
    return 'Sygdom';
  } else if (entry.type === '60') {
    return 'Ferie';
  } else {
    return entry.description || '';
  }
}

function calculateDuration(entry) {
  if (entry.type === '51') {
    const dailyHrs = entry.weekHrs / 5;
    const hours = Math.floor(dailyHrs);
    const minutes = Math.round((dailyHrs - hours) * 60);
    return `${hours} time${hours > 1 ? 'r' : ''} ${minutes} minut${minutes !== 1 ? 'ter' : ''}`.trim();
  }
  
  if (!entry.start_time || !entry.end_time) {
    return '';
  }
  // Konverter 'hh:mm:ss' til minutter
  const [startHours, startMinutes] = entry.start_time.split(':').map(Number);
  const [endHours, endMinutes] = entry.end_time.split(':').map(Number);
  const [breakHours, breakMinutes] = entry.break.split(':').map(Number);

  let startTotalMinutes = startHours * 60 + startMinutes;
  let endTotalMinutes = endHours * 60 + endMinutes;
  let breakTotalMinutes = breakHours * 60 + breakMinutes;

  // Håndter tilfælde, hvor sluttid er efter midnat
  if (endTotalMinutes < startTotalMinutes) {
    endTotalMinutes += 24 * 60; // Tilføj 24 timer
  }

  const durationMinutes = endTotalMinutes - startTotalMinutes - breakTotalMinutes;

  // Beregn timer og resterende minutter
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  // Formater resultatet
  let durationString = '';
  if (hours > 0) {
    durationString += hours + ' time' + (hours > 1 ? 'r' : '') + ' ';
  }
  if (minutes > 0 || hours === 0) {
    durationString += minutes + ' minut' + (minutes !== 1 ? 'ter' : '');
  }

  return durationString.trim();
}

function calculateTotalDuration(timeEntries, selectedUser) {
  const filteredEntries = timeEntries.filter(
    (entry) => selectedUser === 'all' || entry.user_id === selectedUser
  );

  let totalMinutes = 0;

  filteredEntries.forEach((entry) => {
    if (entry.type === '51') {
      const dailyHrs = entry.weekHrs / 5;
      totalMinutes += dailyHrs * 60;
    } else if (entry.start_time && entry.end_time) {
      const [startHours, startMinutes] = entry.start_time.split(':').map(Number);
      const [endHours, endMinutes] = entry.end_time.split(':').map(Number);
      const [breakHours, breakMinutes] = entry.break.split(':').map(Number);

      let startTotalMinutes = startHours * 60 + startMinutes;
      let endTotalMinutes = endHours * 60 + endMinutes;
      let breakTotalMinutes = breakHours * 60 + breakMinutes;

      if (endTotalMinutes < startTotalMinutes) {
        endTotalMinutes += 24 * 60; // Tilføj 24 timer
      }

      totalMinutes += endTotalMinutes - startTotalMinutes - breakTotalMinutes;
    }
  });

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours} time${hours !== 1 ? 'r' : ''} ${minutes} minut${minutes !== 1 ? 'ter' : ''}`;
}

function getPeriodDates() {
    const currentDate = new Date();

    // Beregn datoen "fra" (21. sidste måned)
    const from = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        21
    );

    // Beregn datoen "til" (20. i nuværende måned)
    const to = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        20
    );

    // Formatering af dato til "YYYY-MM-DD"
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Tilføjer 0 foran
        const day = String(date.getDate()).padStart(2, '0');       // Tilføjer 0 foran
        return `${year}-${month}-${day}`;
    };

    return { from: formatDate(from), to: formatDate(to) };
}

function UserDashboard() {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [dateRange, setDateRange] = useState('today');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  useEffect(() => {
    fetchTimeEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dateRange !== 'custom' || (fromDate && toDate)) {
      fetchTimeEntries();
    }
	// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, fromDate, toDate]);

  const fetchTimeEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAccessToken();

      // Byg query string
      let query = '?date_range=' + dateRange;
      if (dateRange === 'custom' && fromDate && toDate) {
		const fromLocalDate = new Date(fromDate.getTime() - fromDate.getTimezoneOffset() * 60000); // Konverter til lokal tid
		const toLocalDate = new Date(toDate.getTime() - toDate.getTimezoneOffset() * 60000); // Konverter til lokal tid
        query += '&from_date=' + fromLocalDate.toISOString().split('T')[0]; // Brug lokal tid, så vi undgår at miste en dag
        query += '&to_date=' + toLocalDate.toISOString().split('T')[0];
      } else if (dateRange === 'current_salary_period') {
        const currentDate = new Date();
        //const from = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 21);
        //const to = new Date(currentDate.getFullYear(), currentDate.getMonth(), 20);
		
		const { from, to } = getPeriodDates();
		
        query += '&from_date=' + from;
        query += '&to_date=' + to;
      }

      const response = await fetch('https://app.recyconelement.dk/api/time/get_all_time_entries.php' + query, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      });
      const result = await response.json();
    
      if (response.ok && result.success) {
        setTimeEntries(result.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } else {
        setError(result.message || 'Fejl ved hentning af tidsregistreringer');
      }
    } catch (error) {
      setError('Der opstod en fejl ved hentning af tidsregistreringer');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const cvrNumber = '42970964';
    const filteredEntries = timeEntries.filter((entry) => (selectedUser === 'all' || entry.user_id === selectedUser) && entry.user_id !== 1);
	console.log(filteredEntries);

    const userTypeDurations = filteredEntries.reduce((acc, entry) => {
      const key = `${entry.user_id}_${entry.type}`;
      if (!acc[key]) {
        acc[key] = new Decimal(0);
      }
      acc[key] = acc[key].plus(calculateDurationInHours(entry));
      return acc;
    }, {});

    const csvRows = Object.entries(userTypeDurations).map(([key, duration]) => {
      const [userId, type] = key.split('_');
      const employeeNumber = userId;
      let wageType = type === '51' ? '02' : type; // Hvis type er '51', sæt wageType til '02' da denne bruges som Sygeløn
      const durationFormatted = duration.toFixed(2).replace('.', '');
      return `${cvrNumber};${employeeNumber};${wageType};${durationFormatted};;`;
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'tidsregistreringer.csv');
  };

  const calculateDurationInHours = (entry) => {
	if (entry.type === '51') {
		const dailyHrs = entry.weekHrs / 5;
		const hours = Math.floor(dailyHrs);
		const minutes = Math.round((dailyHrs - hours) * 60);
		let TotalMinutes = hours * 60 + minutes;
		return new Decimal(TotalMinutes).div(60);
	} else if(entry.type === '60') {
		return 0;
	}
	if (!entry.start_time || !entry.end_time) {
      return 0;
    }
	
    const [startHours, startMinutes] = entry.start_time.split(':').map(Number);
    const [endHours, endMinutes] = entry.end_time.split(':').map(Number);
	const [breakHours, breakMinutes] = entry.break.split(':').map(Number);

    let startTotalMinutes = startHours * 60 + startMinutes;
    let endTotalMinutes = endHours * 60 + endMinutes;
	let breakTotalMinutes = breakHours * 60 + breakMinutes;

    if (endTotalMinutes < startTotalMinutes) { // over midnat
      endTotalMinutes += 24 * 60;
    }

    const durationMinutes = endTotalMinutes - startTotalMinutes - breakTotalMinutes;
    return new Decimal(durationMinutes).div(60);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={da}>
<Container sx={{ mt: 4 }}>
  <Typography variant="h4" gutterBottom>
    Mit Dashboard
  </Typography>

  <Grid container spacing={2} alignItems="center">
    {/* Datointerval */}
    <Grid item xs={12} sm={4}>
      <FormControl fullWidth>
        <InputLabel id="date-range-select-label">Datointerval</InputLabel>
        <Select
          labelId="date-range-select-label"
          value={dateRange}
          label="Datointerval"
          onChange={(e) => setDateRange(e.target.value)}
        >
          <MenuItem value="today">I Dag</MenuItem>
          <MenuItem value="this_week">Denne Uge</MenuItem>
          <MenuItem value="this_month">Denne Måned</MenuItem>
          <MenuItem value="current_salary_period">Lønperiode</MenuItem>
          <MenuItem value="custom">Tilpasset</MenuItem>
        </Select>
      </FormControl>
    </Grid>

    {/* Tilpasset datoer */}
    {dateRange === 'custom' && (
      <>
        <Grid item xs={12} sm={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={daLocale}>
			<DatePicker
			  label="Fra Dato"
			  value={fromDate}
			  onChange={(newValue) => setFromDate(newValue)}
			  slots={{ textField: TextField }}
			  slotProps={{ 
				textField: { fullWidth: true },
				actionBar: {
					actions: ["today", "clear"]
				},
			  }}
			  inputFormat="dd/MM/yyyy"
			/>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={daLocale}>
			<DatePicker
			  label="Til Dato"
			  value={toDate}
			  onChange={(newValue) => setToDate(newValue)}
			  slots={{ textField: TextField }}
			  slotProps={{ 
				textField: { fullWidth: true },
				actionBar: {
					actions: ["today", "clear"]
				},
			  }}
			  inputFormat="dd/MM/yyyy"
			/>
          </LocalizationProvider>
        </Grid>
      </>
    )}

    {/* Brugerfilter */}
    <Grid item xs={12} sm={4}>
      <FormControl fullWidth>
        <InputLabel id="user-select-label">Bruger</InputLabel>
        <Select
          labelId="user-select-label"
          value={selectedUser}
          label="Bruger"
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <MenuItem value="all">Alle Brugere</MenuItem>
          {[...new Map(timeEntries.map((entry) => [entry.user_id, entry])).values()].map((entry) => (
            <MenuItem key={entry.user_id} value={entry.user_id}>
              {entry.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>

    {/* Export CSV knap */}
    <Grid item xs={12} sm={2}>
      <Button variant="contained" onClick={exportToCSV}>
        Eksporter til CSV
      </Button>
    </Grid>
  </Grid>

  {/* Tabel med tidsregistreringer */}
  {loading ? (
    <CircularProgress sx={{ mt: 4 }} />
  ) : error ? (
    <Alert severity="error" sx={{ mt: 4 }}>
      {error}
    </Alert>
  ) : (
  <>
    <Table sx={{ mt: 4 }}>
      <TableHead>
        <TableRow>
          <TableCell>Navn</TableCell>
          <TableCell>Dato</TableCell>
          <TableCell>Start Tid</TableCell>
          <TableCell>Slut Tid</TableCell>
          <TableCell>Beskrivelse</TableCell>
          <TableCell>Varighed (excl. pauser)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {timeEntries
          .filter((entry) => selectedUser === 'all' || entry.user_id === selectedUser)
          .map((entry) => (
            <TableRow key={`${entry.user_id}_${entry.id}`}>
              <TableCell>{entry.name}</TableCell>
              <TableCell>{format(new Date(entry.date), 'dd-MM-yyyy')}</TableCell>
              <TableCell>{entry.start_time ? entry.start_time.slice(0, 5) : ''}</TableCell>
              <TableCell>{entry.end_time ? entry.end_time.slice(0, 5) : ''}</TableCell>
              <TableCell>{getDescription(entry)}</TableCell>
              <TableCell>
                {calculateDuration(entry)}
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>

	<Typography variant="h6" sx={{ mt: 2, textAlign: 'right' }}>
	  Samlet varighed excl. pauser: {calculateTotalDuration(timeEntries, selectedUser)}
	</Typography>
   </>
  )}
</Container>


    </LocalizationProvider>
  );
}

export default UserDashboard;
